import * as React from 'react';
import { Platform, Pressable } from 'react-native';
import { View, Text } from 'react-native';
import { Icon, Button } from 'react-native-elements';
import { CheckBox } from 'react-native-elements/dist/checkbox/CheckBox';
import { useFocusEffect } from '@react-navigation/native';
import * as Print from 'expo-print';
import moment from 'moment';
import { styles } from 'styles/screens/orderDetailsScreen';
import { getOrder, updateOrderStatus, updateCashPayment } from 'services/ordersService';
import OrderDetailsData from 'components/OrderDetailsData';
import OrderDetailStatusModalMenu from 'components/OrderDetailStatusModalMenu';
import VerificationModal from 'components/VerificationModal';
import OrderContext from 'contexts/OrderContext';
import AppContext from 'contexts/AppContext';
import useMediumScreen from 'hooks/useMediumScreen';
import useNotProcessedOrders from 'hooks/useNotProcessedOrders';
import useChangeAuthData from 'hooks/useChangeAuthData';
import Order from 'types/models/Order';
import { getStatusName, getStatusId, getOrderColorByStatus, getTranslatedStatus } from 'types/enums/OrderStatusType';
import OrderDetailsScreenProps from 'types/screens/OrderDetailsScreenProps';
import { useTranslation } from 'react-i18next';
import i18n from '../translation/config';
import UserRoleType from 'types/enums/UserRoleType';
import useResponsiveContainer from 'hooks/useResponsiveContainer';

export default ({ navigation }: OrderDetailsScreenProps): JSX.Element => {
  const { deleteAuthData } = useChangeAuthData();
  const mediumScreen = useMediumScreen();
  const ResponsiveContainer = useResponsiveContainer();
  const { processPendingOrders } = useNotProcessedOrders();
  const { setModalData, authData } = React.useContext(AppContext);
  const { currentOrder, isConnected, currentVenue, currentCommerce } = React.useContext(OrderContext);
  const [order, setOrder] = React.useState<Order | null>(null);
  const originalStatuses = ['Pendiente', 'En Proceso', 'Por Entregar'];
  const [statuses, setStatuses] = React.useState(originalStatuses);
  const [checkedStatus, setCheckedStatus] = React.useState('');
  const [verificationVisible, setVerificationVisible] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [checkedIdentification, setCheckedIdentification] = React.useState(false);
  const [openOrderStateModal, setOpenStateModal] = React.useState<boolean>(false);
  const currentDateTime = mediumScreen ? moment().format('DD/MM/YYYY hh:mm') : null;
  const isParkingCommerce = currentCommerce?.type.description === 'Parking';
  const vehicleData =
    isParkingCommerce && order?.delivery ? order?.delivery.split(',').map((data) => data.split(': ')[1]) : null;

  const isCommerceEditor = authData?.user.role === UserRoleType.CommerceEditor;

  const { t } = useTranslation();

  const printOrder = async () => {
    if (!order) return;
    const htmlContent = createOrderHtml(order);
    try {
      if (Platform.OS === 'web') {
        const iframeWindow = document.getElementById('ifmcontentstoprint') as HTMLIFrameElement;
        if (iframeWindow && iframeWindow.contentWindow) {
          const iframeDocument = iframeWindow.contentDocument;
          if (iframeDocument) {
            iframeDocument.open();
            iframeDocument.write(htmlContent);
            iframeDocument.close();
          }
          iframeWindow.focus();
          iframeWindow.contentWindow.print();
        }
      } else {
        await Print.printAsync({ html: htmlContent, height: 320, width: 320 });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const changeOrderStatus = async (verificationCode: number) => {
    if (!order) return;
    const newStatusId = getStatusId(checkedStatus);

    let updatedOrder: Order = { ...order, status: newStatusId };
    if (verificationCode) {
      updatedOrder = { ...updatedOrder, verificationCode: verificationCode };
    }

    try {
      const result = await updateOrderStatus(updatedOrder);
      if (result) {
        navigation.reset({
          index: 1,
          routes: [{ name: 'VenueSelectionScreen' }, { name: 'OrdersScreen' }],
        });
      } else {
        setError(t('invalid-code'));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const confirmPaymentCash = async (order: Order): Promise<void> => {
    try {
      const result = await updateCashPayment(order);
      if (result) {
        navigation.reset({
          index: 1,
          routes: [{ name: 'VenueSelectionScreen' }, { name: 'OrdersScreen' }],
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  useFocusEffect(
    React.useCallback(() => {
      const fetchOrderDetails = async () => {
        let gotDetails = false;
        if (currentOrder) {
          try {
            const order = await getOrder(currentOrder.id);
            if (order) {
              setOrder(order);
              setCheckedStatus(getStatusName(order.status));
              gotDetails = true;
            }
          } catch (error) {
            console.log(error);
          }
        }
        if (!gotDetails) {
          setTimeout(() => {
            navigation.reset({
              index: 1,
              routes: [isCommerceEditor ? { name: 'OrdersScreen' } : { name: 'CommercesScreen' }],
            });
          }, 500);
        }
      };
      fetchOrderDetails();
    }, []),
  );

  React.useEffect(() => {
    const uploadingPendingOrders = async () => {
      await processPendingOrders();
    };
    uploadingPendingOrders();
  }, [isConnected]);

  React.useEffect(() => {
    if (order) {
      const newStatuses = [...originalStatuses];
      if (order.status === 1 && order.isCash) newStatuses.push('Cancelado');
      if (!(order.isCash && !order.paymentId)) newStatuses.push('Entregado');
      setStatuses(newStatuses);
    }
  }, [order]);

  const processButtonDisabled = (): boolean => {
    return order?.identification !== null && checkedStatus === 'Entregado' && !checkedIdentification;
  };

  if (!order) return <></>;
  return (
    <>
      <ResponsiveContainer style={mediumScreen ? styles.containerLarge : styles.containerSmall}>
        <View style={mediumScreen ? styles.header : styles.headerSmall}>
          <Icon
            name="arrow-back-outline"
            type="ionicon"
            color="#001C47"
            size={40}
            containerStyle={mediumScreen ? styles.arrowIconLarge : styles.arrowIconSmall}
            onPress={() => navigation.navigate('OrdersScreen')}
          />
          {mediumScreen ? (
            <View style={styles.upperTextContainerLarge}>
              <Text style={styles.textTitleLargeOrders}>
                <Text style={[styles.textTitleLargeOrders, styles.textTitleLargeCommerce]}>{`${
                  order.commerce.name
                } > ${t('orders')} > `}</Text>
                {`${t('order')} #${order.id}`}
              </Text>
              <View style={{ marginTop: 25 }}>
                <Text
                  style={{
                    fontSize: 18,
                    fontWeight: '400',
                    lineHeight: 20,
                    textAlign: 'left',
                    color: '#001639',
                  }}
                >{`${currentDateTime}hs`}</Text>
              </View>
            </View>
          ) : (
            <Text style={mediumScreen ? styles.titleTextLarge : styles.titleTextSmall}>{`${order.commerce.name}`}</Text>
          )}
          {mediumScreen && (
            <View style={{ justifyContent: 'flex-end' }}>
              <View style={{ flexDirection: 'row' }}>
                <Text
                  style={{
                    justifyContent: 'center',
                    alignSelf: 'center',
                    fontSize: 16,
                    fontWeight: '400',
                    lineHeight: 20,
                    marginRight: 20,
                    marginBottom: 20,
                  }}
                >
                  {t('status')}
                </Text>
                <Pressable style={{ paddingVertical: 10, alignSelf: 'center' }} onPress={() => setOpenStateModal(true)}>
                  <View
                    style={{
                      paddingVertical: 4,
                      paddingLeft: 4,
                      paddingRight: 21,
                      backgroundColor: getOrderColorByStatus(getStatusId(checkedStatus)) ?? '#FFFFFF',
                      borderRadius: 8,
                      marginBottom: 15,
                    }}
                  >
                    <View style={{ paddingHorizontal: 8, paddingVertical: 8 }}>
                      <Text
                        style={{
                          color: '#FFFFFF',
                          justifyContent: 'center',
                          fontSize: 14,
                          fontWeight: '400',
                          lineHeight: 20,
                          marginLeft: mediumScreen ? 15 : 0,
                          alignSelf: 'center',
                        }}
                      >{`${getTranslatedStatus(checkedStatus)} ↓`}</Text>
                    </View>
                  </View>
                </Pressable>
              </View>
              <OrderDetailStatusModalMenu
                statuses={statuses.sort((statusA, statusB) => {
                  if (statusA !== checkedStatus) {
                    return 1;
                  } else if (statusA === checkedStatus) {
                    return -1;
                  } else if (statusB !== checkedStatus) {
                    return 1;
                  } else if (statusB === checkedStatus) {
                    return -1;
                  }
                  return 0;
                })}
                visible={openOrderStateModal}
                setVisible={setOpenStateModal}
                setCheckedStatus={setCheckedStatus}
                isMediumScreen={mediumScreen}
              />
            </View>
          )}
          <Icon
            name="person-circle-sharp"
            type="ionicon"
            color="#001639"
            size={40}
            containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
            onPress={() =>
              setModalData({
                description: t('logout'),
                generalColor: currentVenue?.appColor ?? '#001C47',
                action: async () => {
                  await deleteAuthData();
                },
              })
            }
          />
        </View>
        {!mediumScreen && (
          <>
            <Text
              style={{
                flexDirection: 'row',
                justifyContent: 'flex-start',
                alignSelf: 'flex-start',
                marginLeft: 35,
                fontSize: 16,
                fontWeight: '500',
                lineHeight: 24,
                textAlign: 'left',
              }}
            >{`Orden #${currentOrder?.id}`}</Text>
            <Text style={{ alignSelf: 'center', fontSize: 16, fontWeight: '400', lineHeight: 20 }}>{t('status')}</Text>
            <Pressable style={{ paddingVertical: 10, alignSelf: 'center' }} onPress={() => setOpenStateModal(true)}>
              <View
                style={{
                  paddingVertical: 4,
                  paddingLeft: 4,
                  paddingRight: 21,
                  backgroundColor: getOrderColorByStatus(getStatusId(checkedStatus)) ?? '#FFFFFF',
                  borderRadius: 8,
                  marginBottom: 15,
                }}
              >
                <View style={{ paddingHorizontal: 8, paddingVertical: 8 }}>
                  <Text
                    style={{
                      color: '#FFFFFF',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: '400',
                      lineHeight: 20,
                      marginLeft: mediumScreen ? 15 : 0,
                      alignSelf: 'center',
                    }}
                  >{`${getTranslatedStatus(checkedStatus)} ↓`}</Text>
                </View>
              </View>
            </Pressable>
            <OrderDetailStatusModalMenu
              statuses={statuses.sort((statusA, statusB) => {
                if (statusA !== checkedStatus) {
                  return 1;
                } else if (statusA === checkedStatus) {
                  return -1;
                } else if (statusB !== checkedStatus) {
                  return 1;
                } else if (statusB === checkedStatus) {
                  return -1;
                }
                return 0;
              })}
              visible={openOrderStateModal}
              setVisible={setOpenStateModal}
              setCheckedStatus={setCheckedStatus}
              isMediumScreen={mediumScreen}
            />
          </>
        )}
        <View style={{ flexDirection: 'row', alignSelf: 'center', justifyContent: 'center', marginBottom: 30 }}>
          <View
            style={{ padding: 4, borderRadius: 16, backgroundColor: '#001639', marginRight: mediumScreen ? 20 : 5 }}
          >
            <View style={{ paddingHorizontal: 8 }}>
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: '400',
                  lineHeight: 20,
                  textAlign: 'center',
                  color: '#FFFFFF',
                }}
              >{`${
                isParkingCommerce
                  ? `${vehicleData && vehicleData[1] ? `${vehicleData[1]} - ` : ''}${vehicleData && vehicleData[0]}`
                  : order.delivery ?? t('pickup-at-store')
              }`}</Text>
            </View>
          </View>
        </View>
        <OrderDetailsData order={order} isParkingOrder={isParkingCommerce} parkingVehicleData={vehicleData} />
        {order.identification && checkedStatus === 'Entregado' && (
          <View
            style={
              mediumScreen ? styles.identificationCheckboxContainerLarge : styles.identificationCheckboxContainerSmall
            }
          >
            <CheckBox
              center={true}
              title={`${t('identification-number.matches')} ${order.identification}?`}
              textStyle={styles.checkBoxText}
              checked={checkedIdentification}
              onPress={() => setCheckedIdentification(!checkedIdentification)}
            />
          </View>
        )}
        <View style={[styles.buttonContainer, mediumScreen && styles.buttonContainerMedium]}>
          {order.isCash && !order.paymentId && (
            <Button
              title={t('confirm-payment')}
              containerStyle={[
                mediumScreen ? styles.btnContainerLarge : styles.btnContainerSmall,
                mediumScreen && styles.leftBtn,
              ]}
              titleStyle={styles.btnActionTitle}
              buttonStyle={{
                backgroundColor: currentVenue?.appColor ?? '#001C47',
                borderColor: currentVenue?.appColor ?? '#001C47',
                borderWidth: 3,
              }}
              onPress={() => {
                if (checkedStatus !== 'Entregado') {
                  setModalData({
                    description: t('confirm-payment'),
                    generalColor: currentVenue?.appColor ?? '#001C47',
                    action: () => {
                      confirmPaymentCash(order);
                    },
                  });
                } else {
                  setVerificationVisible(true);
                }
              }}
            />
          )}
          <Button
            title={t('proccess-order')}
            containerStyle={mediumScreen ? styles.btnContainerLarge : styles.btnContainerSmall}
            titleStyle={styles.btnActionTitle}
            buttonStyle={{
              backgroundColor: currentVenue?.appColor ?? '#001C47',
              borderColor: currentVenue?.appColor ?? '#001C47',
              borderWidth: 3,
            }}
            onPress={() => {
              if (checkedStatus !== 'Entregado') {
                setModalData({
                  description: t('change-order-status'),
                  action: changeOrderStatus,
                  generalColor: currentVenue?.appColor ?? '#001C47',
                });
              } else {
                setVerificationVisible(true);
              }
            }}
            disabled={processButtonDisabled()}
          />
          <Button
            title={t('impress-order')}
            containerStyle={[
              [mediumScreen ? styles.btnContainerLarge : styles.btnContainerSmall, mediumScreen && styles.leftBtn],
            ]}
            titleStyle={styles.btnPrintTitle}
            buttonStyle={[styles.printBtn, { borderColor: currentVenue?.appColor ?? '#001C47' }]}
            onPress={() => {
              printOrder();
            }}
          />
        </View>
      </ResponsiveContainer>
      {Platform.OS === 'web' && <iframe id="ifmcontentstoprint" style={{ display: 'none' }}></iframe>}
      {verificationVisible && (
        <VerificationModal
          title={t('enter-verification-code-optional')}
          visible={verificationVisible}
          setVisible={setVerificationVisible}
          onHandleModal={changeOrderStatus}
          error={error}
          generalColor={currentVenue?.appColor ?? '#001C47'}
        />
      )}
    </>
  );
};

const createOrderHtml = (order: Order): string => {
  let productos = '';
  if (!order.orderDetails) return '';
  for (const orderDetail of order.orderDetails) {
    productos += `<tr>
    <th class="quantity">${orderDetail.quantity}</th>
    <th class="product">${orderDetail.product.name}</th>
    <th class="price">$${orderDetail.price}</th>
    </tr>`;
  }
  return `
  <!DOCTYPE html>
  <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
       <title>${i18n.t('ticket_title')}</title>
      <style>
        @page {
          size: auto; /* auto is the initial value */
  
          /* this affects the margin in the printer settings */
          margin: 25mm 25mm 25mm 25mm;
        }
  
        body {
          /* this affects the margin on the content before sending to printer */
          margin: 2.5mm;
        }
  
        * {
          font-size: 16px;
          font-family: "Times New Roman";
        }
  
        .row {
          margin-bottom: 0.3rem;
        }
  
        .centered {
          text-align: center;
          align-content: center;
        }
  
        .ticket {
          width: 155px;
          max-width: 155px;
        }
  
        td,
        th,
        tr,
        table {
          border-top: 1px solid black;
          border-collapse: collapse;
        }
          
        td.quantity,
        th.quantity {
          width: 40px;
          max-width: 40px;
          word-break: break-all;
        }
  
        td.product,
        th.product {
          width: 75px;
          max-width: 75px;
        }

        td.price,
        th.price {
          width: 40px;
          max-width: 40px;
          word-break: break-all;
        }
      </style>
    </head>
    <body>
      <div class="ticket">
        <div class="row centered"><h4>${i18n.t('buyment-order')}</h4></div>
        <div class="row"><strong>${i18n.t('commerce')}: </strong> ${order.commerce.name}</div>
        <div class="row">
          <strong>${i18n.t('buyer')}: </strong> ${order.buyer != null ? order.buyer.email : order.guestEmail}
        </div>
        <div class="row">
          <strong>Delivery: </strong> ${order.delivery != null ? order.delivery : i18n.t('to-pickup')}
        </div>
        <div class="row"><strong>${i18n.t('commerce')}: </strong> ${order.phone}</div>
        <div class="row"><strong>${i18n.t('order-number')}: </strong> ${order.id}</div>
        <div class="row">
          <strong>${i18n.t('confirmation-code')} </strong> ${order.verificationCode}
        </div>
        <div><strong>${i18n.t('products')}: </strong></div>
        <table class="row">
          <thead>
            <tr>
              <th>${i18n.t('CANT')}</th>
              <th>PROD</th>
              <th>$</th>
            </tr>
          </thead>
          <tbody>
            ${productos}
          </tbody>
        </table>
        <div class="row"><strong>Total: </strong> $${order.totalAmount}</div>
      </div>
    </body>
  </html>  
`;
};
